import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { XmarkCircle } from 'iconoir-react'
import { ReactElement, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { DateComponent } from 'app/components/DateComponent'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import SimpleTable from 'app/components/table/SimpleTable'
import { OpenLinkCell } from 'app/components/table/OpenLinkCell'
import { LocationJson } from 'app/types/entities'
import type { UseOnlineSchedulerReturn } from './useOnlineScheduler'

export type OnlineSchedulerShadcnComponentProps = UseOnlineSchedulerReturn

type OnlineSchedulerRow = LocationJson & { submissionCount?: number; lastSubmission?: string }

export function OnlineSchedulerShadcnComponent({
  locations,
  submissions,
}: OnlineSchedulerShadcnComponentProps): ReactElement {
  const hits = locations?.hits ?? []

  const rows: OnlineSchedulerRow[] = useMemo(
    () =>
      hits.map((location) => ({
        ...location,
        submissionCount: submissions[location.id!]?.count ?? 0,
        lastSubmission: submissions[location.id!]?.last,
      })),
    [hits, submissions],
  )

  const columns = useMemo(
    (): ColumnDef<OnlineSchedulerRow, any>[] => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Location Name`,
        meta: {
          defaultVisible: true,
          // A controlled cell fills the whole cell, so the hover Open button sits at its right edge.
          ControlledComponent: (row) => (
            <OpenLinkCell href={`/settings/online-scheduler/${row?.id}`} label={row?.name ?? ``} />
          ),
        },
      },
      {
        id: `isSchedulerLive`,
        accessorKey: `isSchedulerLive`,
        header: `Scheduler Live`,
        cell: (props) =>
          props.getValue() ? (
            <CheckCircleIcon className="h-5 w-5 text-blue-400" />
          ) : (
            <XmarkCircle className="h-5 w-5 text-red-400" />
          ),
        meta: { defaultVisible: true },
      },
      {
        id: `submissionCount`,
        accessorKey: `submissionCount`,
        header: `Submissions`,
        meta: { defaultVisible: true },
      },
      {
        id: `lastSubmission`,
        accessorKey: `lastSubmission`,
        header: `Last Submission`,
        cell: (props) =>
          props.getValue() ? (
            <DateComponent value={props.getValue()} dateOnly />
          ) : (
            <span className="text-minimal">-</span>
          ),
        meta: { defaultVisible: true },
      },
    ],
    [],
  )

  return (
    <PageScroll
      variant="table"
      header={
        <PageHeaderComponent title="Online Scheduler" itemsText={locations?.total ? `${locations.total}` : undefined} />
      }
    >
      <SimpleTable
        columns={columns}
        data={locations ? rows : undefined}
        hideFooter
        noDataMessage="No locations found"
        edgePadded
      />
    </PageScroll>
  )
}
